// ============================================================
// Home — full landing page (all sections, real content)
// ============================================================

import { Link } from 'wouter';
import { useEffect, useState } from 'react';
import { BrandMark } from '../components/Wordmark';
import Seo from '../components/Seo';
import {
  ArrowRight,
  Inbox,
  Zap,
  TrendingDown,
  MessageSquare,
  BookOpen,
  BarChart3,
  Instagram,
  Facebook,
  Mail,
  Smartphone,
  Globe,
  ShoppingBag,
  Store,
  Wrench,
  Check,
  Plus,
  Minus,
  Sparkles,
  Send,
  Shield,
  Lock,
  UserCheck,
  Download,
  Cpu,
} from 'lucide-react';

export default function Home() {
  // Global scroll-reveal: observe every .rr-fade element on the page once
  // and add `is-visible` when it scrolls into view. Respects reduced motion.
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      document.querySelectorAll('.rr-fade').forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -60px 0px' }
    );

    // Initial pass — Hero usually already in viewport, mark it visible immediately
    document.querySelectorAll('.rr-fade').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Seo
        title="WFS Chatbot · One inbox. Every channel."
        description="Customer messaging for shops that mean business. AI that answers like a senior agent across Instagram, WhatsApp, email and your website."
        path="/"
      />
      <Hero />
      <TrustStrip />
      <Problem />
      <Product />
      <Channels />
      <Integrations />
      <HowItWorks />
      <WhoItsFor />
      <Pricing />
      <BuiltDifferent />
      <FAQ />
      <FinalCTA />
    </>
  );
}

// ============================================================
// 01 — HERO
// ============================================================
function Hero() {
  return (
    <section
      id="hero"
      className="relative isolate flex min-h-[calc(100svh-4rem)] items-center overflow-hidden bg-ink text-white"
    >
      <div className="rr-dot-bg absolute inset-0 -z-10" aria-hidden />
      <div
        className="absolute -top-32 right-0 -z-10 h-[640px] w-[640px] rounded-full opacity-[0.10] blur-3xl"
        style={{ background: '#7CFFB8' }}
        aria-hidden
      />
      <div
        className="absolute -bottom-40 -left-40 -z-10 h-[520px] w-[520px] rounded-full opacity-[0.06] blur-3xl"
        style={{ background: '#7CFFB8' }}
        aria-hidden
      />

      <div className="mx-auto grid w-full max-w-content grid-cols-12 items-center gap-8 px-6 py-10 md:gap-10 md:px-12 md:py-12 lg:gap-8">
        {/* Copy column */}
        <div className="col-span-12 lg:col-span-7">
          <div className="mb-5 inline-flex items-center gap-2 rounded-pill border border-ink-3 bg-ink-2/80 px-3 py-1.5 backdrop-blur-sm">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal" />
            </span>
            <span className="font-mono text-mono-xs uppercase text-chalk">
              Now onboarding new customers
            </span>
          </div>

          {/* Short, punchy, confident. Two lines max at any breakpoint. */}
          <h1 className="font-bold tracking-headline-tight text-white text-[1.875rem] leading-[1.05] sm:text-[2.25rem] md:text-[2.625rem] lg:text-[2.875rem] xl:text-[3.25rem]">
            One inbox.
            <br />
            <span style={{ color: '#7CFFB8' }}>Every channel.</span>
          </h1>

          <p className="mt-5 max-w-xl text-sm text-ash sm:text-base md:text-lg">
            Customer messaging for shops that mean business. Fast, accurate replies from an AI built by the team behind Resin Rock.
          </p>

          <div className="mt-7 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-6">
            <Link
              href="/request-access"
              className="group inline-flex items-center gap-2 rounded-pill px-6 py-3 text-sm font-semibold transition-all hover:brightness-110 hover:shadow-[0_0_32px_rgba(124,255,184,0.5)] sm:text-base"
              style={{ background: '#7CFFB8', color: '#0E1316' }}
            >
              Request access
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
            <a
              href="/"
              className="group inline-flex items-center gap-1.5 text-sm font-medium text-ash transition-colors hover:text-white"
            >
              Already approved?
              <span className="text-white underline-offset-4 group-hover:underline">Sign in</span>
              <span className="transition-transform group-hover:translate-x-0.5">→</span>
            </a>
          </div>

          <div className="mt-6 flex items-center gap-2 overflow-hidden whitespace-nowrap font-mono text-[0.5625rem] uppercase tracking-mono text-graphite sm:gap-3 sm:text-[0.625rem]">
            <span>// two minutes to apply</span>
            <span className="text-ink-3">·</span>
            <span>manually reviewed</span>
            <span className="text-ink-3">·</span>
            <span>2 business days</span>
          </div>
        </div>

        {/* Product preview card column */}
        <div className="col-span-12 lg:col-span-5">
          <div className="mx-auto max-w-md lg:max-w-none">
            <HeroPreviewCard />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroPreviewCard() {
  return (
    <div className="relative">
      {/* depth: stacked offset card behind — only on lg+ where we have horizontal room */}
      <div
        className="absolute inset-0 hidden translate-x-3 translate-y-3 rounded-card border border-ink-3 bg-ink-2/40 lg:block"
        aria-hidden
      />
      <div
        className="absolute inset-0 hidden translate-x-1.5 translate-y-1.5 rounded-card border border-ink-3 bg-ink-2/60 lg:block"
        aria-hidden
      />
      {/* gradient border wrap */}
      <div
        className="absolute -inset-px rounded-card opacity-60"
        style={{
          background:
            'linear-gradient(135deg, rgba(124,255,184,0.7), rgba(124,255,184,0.05) 40%, transparent)',
        }}
        aria-hidden
      />
      <div className="relative rounded-card border border-ink-3 bg-ink-2/90 p-5 backdrop-blur-md shadow-2xl shadow-black/40">
        {/* window chrome */}
        <div className="mb-4 flex items-center justify-between border-b border-ink-3 pb-3">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-ink-3" />
            <span className="h-2 w-2 rounded-full bg-ink-3" />
            <span className="h-2 w-2 rounded-full bg-ink-3" />
          </div>
          <div className="flex items-center gap-2">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal" />
            </span>
            <div className="font-mono text-mono-xs uppercase text-graphite">// live · inbox</div>
          </div>
        </div>

        {/* sample chat thread */}
        <div className="space-y-4">
          {/* customer msg */}
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink-3 font-mono text-mono-xs text-chalk">
              JS
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center gap-2">
                <span className="text-xs font-medium text-chalk">Jamie S.</span>
                <span className="font-mono text-[0.625rem] uppercase text-graphite">via instagram</span>
              </div>
              <div className="rounded-2xl rounded-tl-sm bg-ink-3 px-3 py-2 text-sm text-chalk">
                Hey, do you have the gravel kit in stock for shipping to Toronto?
              </div>
            </div>
          </div>

          {/* AI response */}
          <div className="flex items-start gap-3">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-signal"
              style={{ background: 'rgba(124,255,184,0.12)' }}
            >
              <Sparkles size={14} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center gap-2">
                <span className="text-xs font-medium text-signal">WFS Chatbot</span>
                <span className="font-mono text-[0.625rem] uppercase text-graphite">replied · 0.8s</span>
              </div>
              <div
                className="rounded-2xl rounded-tl-sm border border-signal/20 px-3 py-2 text-sm text-chalk"
                style={{ background: 'rgba(124,255,184,0.06)' }}
              >
                Yes. The 25kg Earth Tone Gravel Kit is in stock and ships to Toronto in 2 to 3
                business days. Want me to send a checkout link?
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button type="button" tabIndex={-1} aria-hidden className="rounded-pill border border-ink-3 px-3 py-1 font-mono text-[0.625rem] uppercase text-chalk transition-colors hover:border-signal/40">
                  send link
                </button>
                <button type="button" tabIndex={-1} aria-hidden className="rounded-pill border border-ink-3 px-3 py-1 font-mono text-[0.625rem] uppercase text-chalk transition-colors hover:border-signal/40">
                  hand to team
                </button>
              </div>
            </div>
          </div>

          {/* tool call indicator */}
          <div className="flex items-center gap-2 rounded-input border border-ink-3 bg-ink/50 px-3 py-2">
            <div className="flex h-5 w-5 items-center justify-center rounded bg-signal/10 text-signal">
              <Cpu size={11} />
            </div>
            <span className="font-mono text-[0.625rem] uppercase text-graphite">
              tool: <span className="text-chalk">checkInventory</span> &nbsp;·&nbsp; sku ETG-25 &nbsp;·&nbsp; <span className="text-signal">in stock</span>
            </span>
          </div>
        </div>

        {/* composer */}
        <div className="mt-5 flex items-center gap-2 rounded-pill border border-ink-3 bg-ink py-1.5 pl-4 pr-1.5">
          <span className="flex-1 text-xs text-graphite">Type a reply…</span>
          <button type="button" tabIndex={-1} aria-hidden className="flex h-7 w-7 items-center justify-center rounded-full bg-signal text-ink">
            <Send size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// 02 — TRUST STRIP
// ============================================================
function TrustStrip() {
  return (
    <section className="relative border-y border-ink-3 bg-ink-2">
      <div className="rr-fade mx-auto max-w-content px-6 py-12 md:px-12 md:py-14">
        <div className="grid items-center gap-10 md:grid-cols-3 md:gap-8">
          {/* Left: parent brand */}
          <div className="text-center md:text-left">
            <div className="font-mono text-mono-xs uppercase text-graphite">
              // a resin rock company
            </div>
            <div className="mt-3 inline-flex items-center gap-3">
              <BrandMark size={36} />
              <span className="text-3xl font-bold tracking-headline-tight text-white md:text-[2rem]">
                Resin&nbsp;Rock
              </span>
            </div>
          </div>

          {/* Center: featured stat */}
          <div className="border-y border-ink-3 py-8 text-center md:border-x md:border-y-0 md:py-2">
            <div className="text-5xl font-bold tracking-headline-tight text-white md:text-6xl">
              30,000<span style={{ color: '#7CFFB8' }}>+</span>
            </div>
            <div className="mt-2 font-mono text-mono-xs uppercase text-graphite">
              installations · north america
            </div>
          </div>

          {/* Right: trust line + link */}
          <div className="flex flex-col items-center gap-3 text-center md:items-start md:text-left">
            <p className="max-w-xs text-sm text-chalk md:text-base">
              Same team building the AI for your inbox.
            </p>
            <a
              href="https://resinrock.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-1.5 rounded-pill border border-ink-3 bg-ink px-3 py-1.5 font-mono text-mono-xs uppercase text-signal transition-all hover:border-signal/40"
            >
              visit resinrock.com
              <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 02b — STATS BAND
// ============================================================
function StatsBand() {
  const stats = [
    { value: '7', label: 'channels', sub: 'one unified inbox' },
    { value: '<2s', label: 'avg reply', sub: 'tier-1 model routing' },
    { value: '100%', label: 'isolated', sub: 'no shared data' },
    { value: '24/7', label: 'always on', sub: 'never sleeps' },
  ];
  return (
    <section className="relative border-b border-ink-3 bg-ink">
      <div className="rr-fade mx-auto max-w-content px-6 py-14 md:px-12 md:py-16">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-4 md:gap-x-10">
          {stats.map((s, i) => (
            <div
              key={s.label}
              className="group relative flex flex-col items-start gap-2"
            >
              {/* mono index */}
              <div className="font-mono text-[0.625rem] uppercase tracking-mono text-graphite">
                /{(i + 1).toString().padStart(2, '0')}
              </div>
              {/* big number */}
              <div className="text-5xl font-bold tracking-headline-tight text-white md:text-6xl lg:text-[4.5rem]">
                {s.value}
              </div>
              {/* label + sub */}
              <div className="mt-1">
                <div className="font-mono text-mono-xs uppercase text-signal">
                  {s.label}
                </div>
                <div className="mt-1 text-xs text-ash">{s.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 03 — PROBLEM (conversion-focused)
// ============================================================
function Problem() {
  const cards = [
    {
      icon: Inbox,
      title: "You're drowning in tabs.",
      body: 'Instagram DMs in one tab. WhatsApp in another. The contact form goes to email. Comments live on Facebook. The Shopify inbox is somewhere else entirely.',
      cost: 'A question that arrives at 9am sits unread until 3pm because no one was watching that tab.',
      symptoms: ['6+ tabs open', 'lost context', 'duplicate replies'],
    },
    {
      icon: Zap,
      title: 'Every minute of silence is a sale walking out.',
      body: 'Customers ask once. If they don\u2019t hear back, they buy from the next shop. The gap between question and answer is the gap between revenue and lost revenue.',
      cost: 'A 4-hour reply window kills the deal. The customer already moved on.',
      symptoms: ['hours not minutes', 'after-hours blackout', 'weekend dead zone'],
    },
    {
      icon: TrendingDown,
      title: 'You\u2019re leaving money on the table.',
      body: "Abandoned carts you'll never recover. Restock questions you'll never see. Returning customers who can't find their order. Every silence is a sale you already earned and lost.",
      cost: 'The shop owner who replies first wins. The one who replies in 4 hours just funded their competitor.',
      symptoms: ['abandoned carts', 'missed restocks', 'lost regulars'],
    },
  ];
  return (
    <section className="relative isolate overflow-hidden bg-ink text-white">
      <div className="rr-dot-bg absolute inset-0 -z-10 opacity-60" aria-hidden />
      <div
        className="absolute -right-32 top-32 -z-10 h-[480px] w-[480px] rounded-full opacity-[0.04] blur-3xl"
        style={{ background: '#7CFFB8' }}
        aria-hidden
      />

      <div className="rr-fade mx-auto max-w-content px-6 py-20 md:px-12 md:py-24">
        {/* Header */}
        <div className="max-w-3xl">
          <span className="rr-tag">// 02 — the problem</span>
          <h2 className="mt-4 text-balance font-bold tracking-headline text-display-sm md:text-display-md">
            Your customers are reaching out.{' '}
            <span style={{ color: '#7CFFB8' }}>You&apos;re losing them.</span>
          </h2>
          <p className="mt-5 max-w-2xl text-base text-ash md:text-lg">
            Three things quietly cost most shops money every single day. None of them are about the wrong answer. They&apos;re all about the answer being too late, in the wrong tab, or never sent at all.
          </p>
        </div>

        {/* Cards */}
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {cards.map(({ icon: Icon, title, body, cost, symptoms }, i) => (
            <div
              key={title}
              className="group relative flex flex-col overflow-hidden rounded-card border border-ink-3 bg-ink-2 p-6 transition-all hover:-translate-y-1 hover:border-signal/30 hover:shadow-2xl hover:shadow-signal/5"
            >
              <div
                className="absolute inset-x-0 top-0 h-px opacity-0 transition-opacity group-hover:opacity-100"
                style={{ background: 'linear-gradient(90deg, transparent, #7CFFB8, transparent)' }}
                aria-hidden
              />
              {/* Header row */}
              <div className="flex items-center justify-between">
                <div
                  className="flex h-11 w-11 items-center justify-center rounded-input border border-signal/20"
                  style={{ background: 'rgba(124,255,184,0.08)' }}
                >
                  <Icon size={20} strokeWidth={1.75} className="text-signal" />
                </div>
                <span className="font-mono text-mono-xs uppercase text-graphite">
                  /0{i + 1}
                </span>
              </div>
              {/* Title */}
              <h3 className="mt-5 text-lg font-semibold text-white">{title}</h3>
              {/* Body */}
              <p className="mt-2 text-sm text-ash">{body}</p>
              {/* Symptom pills */}
              <div className="mt-4 flex flex-wrap gap-1.5">
                {symptoms.map((s) => (
                  <span
                    key={s}
                    className="rounded-pill border border-ink-3 bg-ink/60 px-2.5 py-1 font-mono text-[0.625rem] uppercase tracking-mono text-chalk"
                  >
                    {s}
                  </span>
                ))}
              </div>
              {/* Cost callout */}
              <div className="mt-auto pt-5">
                <div className="border-t border-ink-3 pt-4">
                  <div className="font-mono text-[0.625rem] uppercase text-graphite">
                    // what it actually costs you
                  </div>
                  <div className="mt-2 text-xs italic text-chalk">{cost}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom conversion banner */}
        <div className="relative mt-16">
          <div
            className="absolute -inset-px rounded-card opacity-50"
            style={{
              background:
                'linear-gradient(90deg, transparent, rgba(124,255,184,0.5), transparent)',
            }}
            aria-hidden
          />
          <div className="relative flex flex-col items-center gap-6 rounded-card border border-ink-3 bg-ink-2 p-8 text-center md:flex-row md:justify-between md:p-10 md:text-left">
            <div className="max-w-xl">
              <div className="font-mono text-mono-xs uppercase text-signal">
                // the good news
              </div>
              <h3 className="mt-2 text-xl font-bold tracking-headline text-white md:text-2xl">
                You&apos;re not the problem. The setup is.
              </h3>
              <p className="mt-2 text-sm text-ash md:text-base">
                Every shop hits this wall around the same time. The fix is the same too.
              </p>
            </div>
            <Link
              href="/request-access"
              className="group inline-flex shrink-0 items-center gap-2 rounded-pill px-6 py-3 text-sm font-semibold transition-all hover:brightness-110 hover:shadow-[0_0_28px_rgba(124,255,184,0.5)]"
              style={{ background: '#7CFFB8', color: '#0E1316' }}
            >
              See how it should work
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 04 — PRODUCT (the eye-catching showcase)
// ============================================================
function Product() {
  return (
    <section id="product" className="relative isolate overflow-hidden border-t border-ink-3 bg-ink-2 text-white">
      {/* atmospheric backdrop */}
      <div className="rr-dot-bg absolute inset-0 -z-10 opacity-50" aria-hidden />
      <div
        className="absolute right-1/4 top-32 -z-10 h-[500px] w-[500px] rounded-full opacity-[0.05] blur-3xl"
        style={{ background: '#7CFFB8' }}
        aria-hidden
      />

      <div className="rr-fade mx-auto max-w-content px-6 py-20 md:px-12 md:py-28">
        {/* Header */}
        <div className="max-w-3xl">
          <span className="rr-tag">// 03 — what it actually looks like</span>
          <h2 className="mt-4 text-balance font-bold tracking-headline text-display-md md:text-display-lg">
            One inbox. Built around <span style={{ color: '#7CFFB8' }}>how shops work.</span>
          </h2>
          <p className="mt-5 text-base text-ash md:text-lg">
            Four surfaces. One product. Real screens, not marketing renders.
          </p>
        </div>

        {/* Three feature rows */}
        <div className="mt-20 space-y-24 md:space-y-32">
          <ProductRow
            index="01"
            label="// inbox"
            icon={Inbox}
            title="One thread per customer."
            body="Instagram, WhatsApp, Facebook, TikTok, email, SMS, your website. Every channel funnels into the same conversation, in the same order, with the same context."
            features={['7 channels supported', 'Real-time threading', 'Smart routing', 'Team handoff']}
            mock={<InboxMock />}
            reverse={false}
          />
          <ProductRow
            index="02"
            label="// knowledge"
            icon={BookOpen}
            title="Your products. Your voice."
            body="Point WFS Chatbot at your site and your catalogue. It indexes every page, every product, every policy. Then it answers in your voice."
            features={['Auto-crawl your site', 'Live product sync', 'Custom tone', 'No hallucinations']}
            mock={<KnowledgeMock />}
            reverse={true}
          />
          <ProductRow
            index="03"
            label="// analytics"
            icon={BarChart3}
            title="See what your AI is actually doing."
            body="Volume by channel, response times, what got resolved, what got handed to your team. Real numbers from real conversations, updated live."
            features={['Per-channel breakdowns', 'Response time SLAs', 'Escalation tracking', 'Export anytime']}
            mock={<AnalyticsMock />}
            reverse={false}
          />
          <ProductRow
            index="04"
            label="// guardrails"
            icon={Shield}
            title="The boring stuff that breaks AI tools."
            body="Rate limits, spam filters, AI disclosure rules, prompt-injection defense, per-user token budgets. Enterprise-grade plumbing, on by default."
            features={['Multi-tenant isolation', 'Spam & abuse filters', 'AI disclosure rules', 'Prompt injection defense', 'Per-user cost caps', 'Live token tracking']}
            mock={<GuardrailsMock />}
            reverse={true}
          />
        </div>
      </div>
    </section>
  );
}

function ProductRow({
  index, label, icon: Icon, title, body, features, mock, reverse,
}: {
  index: string;
  label: string;
  icon: typeof Inbox;
  title: string;
  body: string;
  features: string[];
  mock: React.ReactNode;
  reverse: boolean;
}) {
  return (
    <div className={`grid items-center gap-12 md:grid-cols-12 md:gap-16 ${reverse ? 'md:[direction:rtl]' : ''}`}>
      {/* Copy column — 5 of 12 */}
      <div className="md:col-span-5 md:[direction:ltr]">
        <div className="flex items-center gap-3">
          <span className="font-mono text-mono-xs uppercase text-graphite">{label}</span>
          <span className="h-px flex-1 bg-ink-3" aria-hidden />
          <span className="font-mono text-mono-xs uppercase text-signal">/{index}</span>
        </div>
        <div className="mt-6 flex items-center gap-3">
          <div
            className="flex h-11 w-11 items-center justify-center rounded-input border border-signal/20"
            style={{ background: 'rgba(124,255,184,0.08)' }}
          >
            <Icon size={20} strokeWidth={1.75} className="text-signal" />
          </div>
        </div>
        <h3 className="mt-5 text-balance text-3xl font-bold tracking-headline-tight text-white md:text-4xl">
          {title}
        </h3>
        <p className="mt-4 text-base text-ash md:text-lg">{body}</p>
        <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2.5">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-chalk">
              <Check size={15} className="mt-0.5 shrink-0 text-signal" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>
      {/* Mock column — 7 of 12 */}
      <div className="md:col-span-7 md:[direction:ltr]">{mock}</div>
    </div>
  );
}

/** Premium "device frame" wrapper. Stacked depth, gradient border, top chrome. */
function MockFrame({ children, label }: { children: React.ReactNode; label?: string }) {
  return (
    <div className="relative">
      {/* stacked depth shadows */}
      <div className="absolute inset-0 translate-x-3 translate-y-3 rounded-card border border-ink-3 bg-ink-2/30" aria-hidden />
      <div className="absolute inset-0 translate-x-1.5 translate-y-1.5 rounded-card border border-ink-3 bg-ink-2/50" aria-hidden />
      {/* gradient border */}
      <div
        className="absolute -inset-px rounded-card opacity-60"
        style={{
          background:
            'linear-gradient(135deg, rgba(124,255,184,0.6), rgba(124,255,184,0.05) 35%, transparent)',
        }}
        aria-hidden
      />
      {/* main frame */}
      <div className="relative overflow-hidden rounded-card border border-ink-3 bg-ink shadow-2xl shadow-black/40">
        {/* chrome bar */}
        <div className="flex items-center justify-between border-b border-ink-3 bg-ink-2/60 px-4 py-3">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-ink-3" />
            <span className="h-2 w-2 rounded-full bg-ink-3" />
            <span className="h-2 w-2 rounded-full bg-ink-3" />
          </div>
          {label && (
            <div className="flex items-center gap-2">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-50" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal" />
              </span>
              <span className="font-mono text-[0.625rem] uppercase tracking-mono text-graphite">
                {label}
              </span>
            </div>
          )}
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function InboxMock() {
  const threads = [
    { name: 'Jamie S.', msg: 'Do you ship to Toronto?', via: 'instagram', time: '2m', unread: true, active: true },
    { name: 'Maria L.', msg: 'Order #4471 status?', via: 'whatsapp', time: '12m', unread: false },
    { name: 'Devon P.', msg: 'Refund question on…', via: 'email', time: '34m', unread: false },
    { name: 'Aiko T.', msg: 'Can the kit handle…', via: 'web', time: '1h', unread: false },
    { name: 'Pat R.', msg: 'Color match advice?', via: 'tiktok', time: '2h', unread: false },
  ];
  return (
    <MockFrame label="// inbox · live">
      {/* filter pills — wrap on narrow */}
      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        {['all', 'unread', 'ig', 'wa', 'email'].map((p, i) => (
          <span
            key={p}
            className={`shrink-0 rounded-pill border px-2.5 py-1 font-mono text-[0.625rem] uppercase tracking-mono ${
              i === 0
                ? 'border-signal/40 text-signal'
                : 'border-ink-3 text-graphite'
            }`}
            style={i === 0 ? { background: 'rgba(124,255,184,0.06)' } : undefined}
          >
            {p}
          </span>
        ))}
      </div>
      {/* threads */}
      <div className="space-y-1.5">
        {threads.map((t) => (
          <div
            key={t.name}
            className={`flex items-center gap-3 rounded-input p-2.5 transition-colors ${
              t.active ? 'ring-1 ring-signal/30' : 'hover:bg-ink-2'
            }`}
            style={t.active ? { background: 'rgba(124,255,184,0.05)' } : undefined}
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink-3 font-mono text-mono-xs text-chalk">
              {t.name.split(' ').map((s) => s[0]).join('')}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium text-white">{t.name}</span>
                <span className="font-mono text-[0.625rem] uppercase text-graphite">{t.time}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="truncate text-xs text-ash">{t.msg}</span>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="font-mono text-[0.625rem] uppercase text-graphite">{t.via}</span>
              {t.unread && <span className="h-1.5 w-1.5 rounded-full bg-signal" />}
            </div>
          </div>
        ))}
      </div>
      {/* footer status */}
      <div className="mt-4 flex items-center justify-between border-t border-ink-3 pt-3">
        <div className="font-mono text-[0.625rem] uppercase text-graphite">
          // 5 conversations &nbsp;·&nbsp; 1 unread
        </div>
        <div className="font-mono text-[0.625rem] uppercase text-signal">live</div>
      </div>
    </MockFrame>
  );
}

function KnowledgeMock() {
  const sources = [
    { label: 'resinrock.com', count: '142 pages', status: 'synced' },
    { label: 'product catalogue', count: '38 SKUs', status: 'synced' },
    { label: 'shipping & returns', count: '12 sections', status: 'synced' },
    { label: 'install guide', count: '24 steps', status: 'syncing' },
  ];
  return (
    <MockFrame label="// knowledge · indexing">
      {/* stats row */}
      <div className="mb-4 grid grid-cols-3 gap-3 border-b border-ink-3 pb-4">
        <div>
          <div className="font-mono text-[0.625rem] uppercase text-graphite">// sources</div>
          <div className="mt-1 text-2xl font-bold text-white">4</div>
        </div>
        <div>
          <div className="font-mono text-[0.625rem] uppercase text-graphite">// chunks</div>
          <div className="mt-1 text-2xl font-bold text-white">2,184</div>
        </div>
        <div>
          <div className="font-mono text-[0.625rem] uppercase text-graphite">// vectors</div>
          <div className="mt-1 text-2xl font-bold" style={{ color: '#7CFFB8' }}>
            indexed
          </div>
        </div>
      </div>
      {/* source list */}
      <div className="space-y-2">
        {sources.map((s) => (
          <div
            key={s.label}
            className="flex items-center gap-3 rounded-input border border-ink-3 bg-ink-2 p-3"
          >
            <div
              className="flex h-9 w-9 items-center justify-center rounded text-signal"
              style={{ background: 'rgba(124,255,184,0.08)' }}
            >
              <BookOpen size={15} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-white">{s.label}</div>
              <div className="font-mono text-[0.625rem] uppercase text-graphite">
                {s.count} indexed
              </div>
            </div>
            {s.status === 'synced' ? (
              <div className="flex items-center gap-1.5">
                <Check size={13} className="text-signal" />
                <span className="font-mono text-[0.625rem] uppercase text-signal">synced</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-60" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal" />
                </span>
                <span className="font-mono text-[0.625rem] uppercase text-signal">syncing</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </MockFrame>
  );
}

function GuardrailsMock() {
  const rules = [
    { label: 'AI disclosure on first reply', status: 'active' },
    { label: 'Rate limit · 30 msgs / hour / contact', status: 'active' },
    { label: 'Spam filter · keyword + ML', status: 'active' },
    { label: 'Prompt injection guard', status: 'active' },
  ];
  const models = [
    { name: 'haiku', share: 72, cost: '$0.31' },
    { name: 'sonnet', share: 24, cost: '$1.84' },
    { name: 'opus', share: 4, cost: '$0.92' },
  ];
  return (
    <MockFrame label="// guardrails · armed">
      {/* Top status grid */}
      <div className="grid grid-cols-3 gap-3 border-b border-ink-3 pb-4">
        <div>
          <div className="font-mono text-[0.625rem] uppercase text-graphite">// blocked</div>
          <div className="mt-1 text-2xl font-bold text-white">147</div>
          <div className="text-[0.625rem] uppercase tracking-mono text-ash">spam · 24h</div>
        </div>
        <div>
          <div className="font-mono text-[0.625rem] uppercase text-graphite">// rules</div>
          <div className="mt-1 text-2xl font-bold" style={{ color: '#7CFFB8' }}>
            4 / 4
          </div>
          <div className="text-[0.625rem] uppercase tracking-mono text-ash">all active</div>
        </div>
        <div>
          <div className="font-mono text-[0.625rem] uppercase text-graphite">// budget</div>
          <div className="mt-1 text-2xl font-bold text-white">$3.07</div>
          <div className="text-[0.625rem] uppercase tracking-mono text-ash">31% of cap</div>
        </div>
      </div>

      {/* Active rules list */}
      <div className="mt-4">
        <div className="mb-2 font-mono text-[0.625rem] uppercase text-graphite">
          // active prompting layers
        </div>
        <div className="space-y-1.5">
          {rules.map((r) => (
            <div
              key={r.label}
              className="flex items-center justify-between rounded-input border border-ink-3 bg-ink-2 px-3 py-2"
            >
              <div className="flex items-center gap-2.5">
                <Lock size={12} className="text-signal" />
                <span className="text-xs text-chalk">{r.label}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-50" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal" />
                </span>
                <span className="font-mono text-[0.625rem] uppercase text-signal">on</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cost / model breakdown */}
      <div className="mt-4 border-t border-ink-3 pt-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="font-mono text-[0.625rem] uppercase text-graphite">
            // model routing · 24h
          </div>
          <div className="font-mono text-[0.625rem] uppercase text-signal">$3.07 spent</div>
        </div>
        {/* segmented bar */}
        <div className="flex h-2 overflow-hidden rounded-full bg-ink-3">
          <div
            className="h-full"
            style={{ width: '72%', background: '#7CFFB8' }}
          />
          <div
            className="h-full"
            style={{ width: '24%', background: 'rgba(124,255,184,0.55)' }}
          />
          <div
            className="h-full"
            style={{ width: '4%', background: 'rgba(124,255,184,0.25)' }}
          />
        </div>
        {/* legend */}
        <div className="mt-3 grid grid-cols-3 gap-2">
          {models.map((m, i) => (
            <div key={m.name} className="flex items-center gap-2">
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{
                  background: i === 0 ? '#7CFFB8' : i === 1 ? 'rgba(124,255,184,0.55)' : 'rgba(124,255,184,0.25)',
                }}
              />
              <div className="min-w-0">
                <div className="font-mono text-[0.625rem] uppercase text-chalk">
                  {m.name} · {m.share}%
                </div>
                <div className="font-mono text-[0.625rem] uppercase text-graphite">{m.cost}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </MockFrame>
  );
}

function AnalyticsMock() {
  const bars = [42, 68, 51, 78, 55, 92, 73];
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  return (
    <MockFrame label="// analytics · live">
      {/* top stats */}
      <div className="grid grid-cols-3 gap-4 border-b border-ink-3 pb-5">
        <div>
          <div className="font-mono text-[0.625rem] uppercase text-graphite">// 7 days</div>
          <div className="mt-1 text-2xl font-bold text-white">2,184</div>
          <div className="text-[0.625rem] uppercase tracking-mono text-ash">conversations</div>
        </div>
        <div>
          <div className="font-mono text-[0.625rem] uppercase text-graphite">// avg reply</div>
          <div className="mt-1 text-2xl font-bold" style={{ color: '#7CFFB8' }}>
            1.2s
          </div>
          <div className="text-[0.625rem] uppercase tracking-mono text-ash">tier-1 routed</div>
        </div>
        <div>
          <div className="font-mono text-[0.625rem] uppercase text-graphite">// resolved</div>
          <div className="mt-1 text-2xl font-bold text-white">94%</div>
          <div className="text-[0.625rem] uppercase tracking-mono text-ash">no handoff</div>
        </div>
      </div>
      {/* bar chart */}
      <div className="mt-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="font-mono text-[0.625rem] uppercase text-graphite">
            // conversations / day
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-signal" />
            <span className="font-mono text-[0.625rem] uppercase text-graphite">live</span>
          </div>
        </div>
        <div className="flex h-28 items-end gap-2">
          {bars.map((h, i) => (
            <div key={i} className="group flex flex-1 flex-col items-center gap-1.5">
              <div className="relative w-full">
                <div
                  className="w-full rounded-t"
                  style={{
                    height: `${h * 1.1}px`,
                    background: 'linear-gradient(to top, rgba(124,255,184,0.2), rgba(124,255,184,0.7))',
                  }}
                />
              </div>
              <span className="font-mono text-[0.625rem] uppercase text-graphite">{days[i]}</span>
            </div>
          ))}
        </div>
      </div>
      {/* breakdown row */}
      <div className="mt-5 flex items-center justify-between border-t border-ink-3 pt-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-signal" />
            <span className="font-mono text-[0.625rem] uppercase text-graphite">handled</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-ink-3" />
            <span className="font-mono text-[0.625rem] uppercase text-graphite">handed off</span>
          </div>
        </div>
        <div className="font-mono text-[0.625rem] uppercase text-signal">+12% wow</div>
      </div>
    </MockFrame>
  );
}

// ============================================================
// 05 — CHANNELS
// ============================================================
function Channels() {
  // Messaging channels only (e-commerce integrations live in §05b)
  const marqueeChannels = [
    { Icon: Instagram, name: 'Instagram' },
    { Icon: Facebook, name: 'Facebook' },
    { Icon: MessageSquare, name: 'WhatsApp' },
    { Icon: MessageSquare, name: 'TikTok' },
    { Icon: Mail, name: 'Email' },
    { Icon: Smartphone, name: 'SMS' },
    { Icon: Globe, name: 'Web widget' },
  ];
  const list = [...marqueeChannels, ...marqueeChannels, ...marqueeChannels];

  // Category cards
  const categories = [
    {
      Icon: MessageSquare,
      tag: '// social dms',
      title: 'Social messaging',
      channels: ['Instagram', 'Facebook', 'WhatsApp', 'TikTok'],
      body: 'Reply to DMs and comments without leaving the inbox. Threading by customer, not by platform.',
    },
    {
      Icon: Mail,
      tag: '// direct',
      title: 'Email & SMS',
      channels: ['Email (any address)', 'SMS via Twilio'],
      body: 'IMAP / SMTP for any email box. SMS via Twilio. Both feed the same conversation thread.',
    },
    {
      Icon: Globe,
      tag: '// owned',
      title: 'Web widget',
      channels: ['Any website', 'Shadow DOM', 'Mobile-first'],
      body: 'A single embeddable script. Works on any site, any framework, no developer required.',
    },
  ];

  // Live activity ticker
  const ticker = [
    { ch: 'instagram', name: 'Jamie S.', action: 'replied · 0.8s' },
    { ch: 'whatsapp', name: 'Maria L.', action: 'order lookup · 1.1s' },
    { ch: 'email', name: 'Devon P.', action: 'handed off · agent' },
    { ch: 'web', name: 'Aiko T.', action: 'replied · 0.9s' },
  ];

  return (
    <section id="channels" className="relative isolate overflow-hidden border-t border-ink-3 bg-ink text-white">
      <div className="rr-dot-bg absolute inset-0 -z-10 opacity-50" aria-hidden />
      <div
        className="absolute -right-32 top-32 -z-10 h-[480px] w-[480px] rounded-full opacity-[0.05] blur-3xl"
        style={{ background: '#7CFFB8' }}
        aria-hidden
      />

      {/* Header — asymmetric: copy left, live ticker right */}
      <div className="rr-fade mx-auto max-w-content px-6 py-20 md:px-12 md:py-24">
        <div className="grid items-end gap-12 md:grid-cols-12 md:gap-16">
          <div className="md:col-span-7">
            <span className="rr-tag">// 04 — channels</span>
            <h2 className="mt-4 max-w-3xl text-balance font-bold tracking-headline text-display-md md:text-display-lg">
              Meet customers <span style={{ color: '#7CFFB8' }}>where they are.</span>
            </h2>
            <p className="mt-5 max-w-xl text-base text-ash md:text-lg">
              Seven messaging channels, one inbox. Every channel feeds the same threaded conversation, with the same context, in the same place.
            </p>
          </div>

          {/* Live activity ticker card */}
          <div className="md:col-span-5">
            <div className="relative">
              <div
                className="absolute -inset-px rounded-card opacity-50"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(124,255,184,0.5), rgba(124,255,184,0.05) 40%, transparent)',
                }}
                aria-hidden
              />
              <div className="relative rounded-card border border-ink-3 bg-ink-2/80 p-4 backdrop-blur-sm">
                <div className="mb-3 flex items-center justify-between">
                  <span className="font-mono text-mono-xs uppercase text-graphite">
                    // live activity
                  </span>
                  <div className="flex items-center gap-1.5">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-60" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal" />
                    </span>
                    <span className="font-mono text-mono-xs uppercase text-signal">live</span>
                  </div>
                </div>
                <div className="space-y-2">
                  {ticker.map((t, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 rounded-input border border-ink-3 bg-ink/60 px-3 py-2"
                    >
                      <span className="font-mono text-[0.625rem] uppercase text-signal w-16">
                        {t.ch}
                      </span>
                      <span className="text-xs text-chalk flex-1 truncate">{t.name}</span>
                      <span className="font-mono text-[0.625rem] uppercase text-graphite">
                        {t.action}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Category cards */}
        <div className="mt-16 grid gap-5 md:grid-cols-3">
          {categories.map((cat) => (
            <div
              key={cat.title}
              className="group relative overflow-hidden rounded-card border border-ink-3 bg-ink-2 p-6 transition-all hover:-translate-y-1 hover:border-signal/30"
            >
              <div
                className="absolute inset-x-0 top-0 h-px opacity-0 transition-opacity group-hover:opacity-100"
                style={{ background: 'linear-gradient(90deg, transparent, #7CFFB8, transparent)' }}
                aria-hidden
              />
              <div className="flex items-center justify-between">
                <div
                  className="flex h-11 w-11 items-center justify-center rounded-input border border-signal/20"
                  style={{ background: 'rgba(124,255,184,0.08)' }}
                >
                  <cat.Icon size={20} strokeWidth={1.75} className="text-signal" />
                </div>
                <span className="font-mono text-mono-xs uppercase text-graphite">{cat.tag}</span>
              </div>
              <h3 className="mt-5 text-lg font-semibold text-white">{cat.title}</h3>
              <p className="mt-2 text-sm text-ash">{cat.body}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {cat.channels.map((c) => (
                  <span
                    key={c}
                    className="rounded-pill border border-ink-3 bg-ink/60 px-2.5 py-1 font-mono text-[0.625rem] uppercase tracking-mono text-chalk"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Drifting strip — the only animation on the page */}
      <div className="relative overflow-hidden border-y border-ink-3 bg-ink-2 py-8">
        <div
          className="absolute inset-y-0 left-0 z-10 w-32"
          style={{ background: 'linear-gradient(to right, #161C20, transparent)' }}
          aria-hidden
        />
        <div
          className="absolute inset-y-0 right-0 z-10 w-32"
          style={{ background: 'linear-gradient(to left, #161C20, transparent)' }}
          aria-hidden
        />
        <div className="rr-drift flex w-max gap-8 px-6">
          {list.map((c, i) => (
            <div
              key={i}
              className="flex shrink-0 items-center gap-2.5 rounded-pill border border-ink-3 bg-ink/60 px-5 py-2.5 transition-colors hover:border-signal/30"
            >
              <c.Icon size={16} strokeWidth={1.75} className="text-signal" />
              <span className="font-mono text-mono-xs uppercase text-chalk">{c.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom stat row — single line on all sizes */}
      <div className="rr-fade mx-auto max-w-content px-6 py-8 md:px-12">
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center font-mono text-[0.625rem] uppercase tracking-mono">
          <span className="text-graphite">// 7 channels</span>
          <span className="text-ink-3">·</span>
          <span className="text-graphite">// 1 inbox</span>
          <span className="text-ink-3">·</span>
          <span className="text-signal">// 0 missed messages</span>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 05b — INTEGRATIONS GRID
// ============================================================
function Integrations() {
  const items = [
    {
      Icon: ShoppingBag,
      name: 'Shopify',
      tag: '// app store',
      installType: 'Shopify App Store',
      installSteps: ['Click install', 'Approve scopes', 'Paste API key'],
      body: 'Embedded admin in your Shopify dashboard. One-click install, full product + customer + order sync.',
      capabilities: ['Product sync', 'Order lookup', 'Customer history', 'Embedded admin', 'OAuth install'],
      version: 'v1.4',
    },
    {
      Icon: Store,
      name: 'WooCommerce',
      tag: '// wp plugin',
      installType: 'WordPress Plugin',
      installSteps: ['Upload .zip', 'Activate', 'Paste API key'],
      body: 'Full WooCommerce sync with real-time cart tracking and abandoned-cart hooks. No third-party email tool needed.',
      capabilities: ['Product sync', 'Cart tracking', 'Order webhooks', 'Customer sync', 'Native hooks'],
      version: 'v1.2',
    },
    {
      Icon: Globe,
      name: 'WordPress',
      tag: '// wp plugin',
      installType: 'WordPress Plugin',
      installSteps: ['Upload .zip', 'Activate', 'Paste API key'],
      body: 'Drop-in chat widget on every page, knowledge crawl from your site, admin dashboard inside WordPress. Works on any theme.',
      capabilities: ['Site widget', 'Knowledge crawl', 'Admin dashboard', 'Any theme', 'Page exclusions'],
      version: 'v1.2',
    },
  ];

  return (
    <section className="relative isolate overflow-hidden border-t border-ink-3 bg-ink-2 text-white">
      <div className="rr-dot-bg absolute inset-0 -z-10 opacity-40" aria-hidden />
      <div
        className="absolute -left-32 top-32 -z-10 h-[480px] w-[480px] rounded-full opacity-[0.05] blur-3xl"
        style={{ background: '#7CFFB8' }}
        aria-hidden
      />

      <div className="rr-fade mx-auto max-w-content px-6 py-20 md:px-12 md:py-24">
        {/* Header */}
        <div className="grid items-end gap-10 md:grid-cols-12">
          <div className="md:col-span-8">
            <span className="rr-tag">// 05 — first-class integrations</span>
            <h2 className="mt-4 text-balance font-bold tracking-headline text-display-md md:text-display-lg">
              Plugs into the platforms <span style={{ color: '#7CFFB8' }}>shops actually use.</span>
            </h2>
            <p className="mt-5 max-w-xl text-base text-ash md:text-lg">
              Native installers for Shopify, WooCommerce and WordPress. No developer required, no copy-paste of widget snippets.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <div className="inline-flex flex-col items-start gap-2 rounded-card border border-ink-3 bg-ink/60 p-4 md:items-end">
              <div className="font-mono text-mono-xs uppercase text-graphite">// install time</div>
              <div className="text-3xl font-bold tracking-headline-tight text-white">
                ~ 5<span style={{ color: '#7CFFB8' }}>min</span>
              </div>
              <div className="font-mono text-[0.625rem] uppercase tracking-mono text-ash">
                from install to live
              </div>
            </div>
          </div>
        </div>

        {/* Cards */}
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {items.map((it) => (
            <div
              key={it.name}
              className="group relative flex flex-col overflow-hidden rounded-card border border-ink-3 bg-ink p-6 transition-all hover:-translate-y-1 hover:border-signal/30"
            >
              {/* gradient top line */}
              <div
                className="absolute inset-x-0 top-0 h-px opacity-30 transition-opacity group-hover:opacity-100"
                style={{ background: 'linear-gradient(90deg, transparent, #7CFFB8, transparent)' }}
                aria-hidden
              />

              {/* Header row */}
              <div className="flex items-center justify-between">
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-input border border-signal/20"
                  style={{ background: 'rgba(124,255,184,0.08)' }}
                >
                  <it.Icon size={22} strokeWidth={1.75} className="text-signal" />
                </div>
                <div className="flex flex-col items-end gap-0.5">
                  <span className="font-mono text-mono-xs uppercase text-graphite">{it.tag}</span>
                  <span className="font-mono text-[0.625rem] uppercase text-signal">{it.version}</span>
                </div>
              </div>

              {/* Title */}
              <h3 className="mt-5 text-2xl font-bold text-white">{it.name}</h3>
              <p className="mt-2 text-sm text-ash">{it.body}</p>

              {/* Install steps */}
              <div className="mt-5 rounded-input border border-ink-3 bg-ink-2 p-3">
                <div className="mb-2 font-mono text-[0.625rem] uppercase text-graphite">
                  // install
                </div>
                <ol className="space-y-1.5">
                  {it.installSteps.map((step, i) => (
                    <li key={step} className="flex items-center gap-2 text-xs text-chalk">
                      <span
                        className="flex h-4 w-4 items-center justify-center rounded-full font-mono text-[0.5625rem] font-semibold"
                        style={{ background: 'rgba(124,255,184,0.12)', color: '#7CFFB8' }}
                      >
                        {i + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Capabilities */}
              <div className="mt-4">
                <div className="mb-2 font-mono text-[0.625rem] uppercase text-graphite">
                  // capabilities
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {it.capabilities.map((c) => (
                    <span
                      key={c}
                      className="rounded-pill border border-ink-3 bg-ink/60 px-2.5 py-1 font-mono text-[0.625rem] uppercase tracking-mono text-chalk"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>

              {/* Footer status */}
              <div className="mt-auto flex items-center justify-between border-t border-ink-3 pt-4">
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-50" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal" />
                  </span>
                  <span className="font-mono text-mono-xs uppercase text-signal">available now</span>
                </div>
                <span className="font-mono text-[0.625rem] uppercase text-graphite">{it.installType}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 06 — HOW IT WORKS
// ============================================================
function HowItWorks() {
  const steps = [
    {
      n: '01',
      title: 'Apply',
      time: '2 min',
      body: 'A short form. Tell us who you are and what you sell.',
      actions: [
        'Fill the request form',
        'No credit card',
        'Honest answers, no sales pitch',
      ],
    },
    {
      n: '02',
      title: 'Get approved',
      time: '≤ 2 days',
      body: 'A real person reviews every request. You get a secure magic link by email.',
      actions: [
        'Manual review by a human',
        'Magic link to set your password',
        'Single-tenant account created',
      ],
    },
    {
      n: '03',
      title: 'Connect & train',
      time: '~ 30 min',
      body: 'Plug in your channels, sync your store, crawl your site, set the voice.',
      actions: [
        'Connect messaging channels',
        'Sync store + product catalogue',
        'Crawl your site for knowledge',
        'Set tone, persona & guardrails',
      ],
    },
    {
      n: '04',
      title: 'Go live',
      time: '1 click',
      body: 'Test in the sandbox, then flip the switch. Your AI handles customers from minute one.',
      actions: [
        'Preview replies in the sandbox',
        'Flip the live switch',
        'Watch the inbox in real time',
      ],
    },
  ];
  return (
    <section id="how-it-works" className="relative border-t border-ink-3 bg-ink text-white">
      <div className="rr-dot-bg absolute inset-0 -z-10 opacity-50" aria-hidden />
      <div className="rr-fade mx-auto max-w-content px-6 py-20 md:px-12 md:py-24">
        {/* Header */}
        <div className="grid items-end gap-10 md:grid-cols-12">
          <div className="md:col-span-8">
            <span className="rr-tag">// 06 — how it works</span>
            <h2 className="mt-4 max-w-3xl text-balance font-bold tracking-headline text-display-md md:text-display-lg">
              From request to live <span style={{ color: '#7CFFB8' }}>in four real steps.</span>
            </h2>
            <p className="mt-5 max-w-xl text-base text-ash md:text-lg">
              No demo calls, no sales pitch, no &quot;we&apos;ll get back to you next quarter.&quot; Real onboarding actions, real time estimates.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <div className="inline-flex flex-col items-start gap-2 rounded-card border border-ink-3 bg-ink-2 p-4 md:items-end">
              <div className="font-mono text-mono-xs uppercase text-graphite">// total time</div>
              <div className="text-3xl font-bold tracking-headline-tight text-white">
                ~ 35<span style={{ color: '#7CFFB8' }}>min</span>
              </div>
              <div className="font-mono text-[0.625rem] uppercase tracking-mono text-ash">
                + 2 days for review
              </div>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="relative mt-16">
          {/* connecting line, desktop only */}
          <div
            className="absolute left-0 right-0 top-[78px] hidden h-px lg:block"
            style={{
              background:
                'linear-gradient(90deg, transparent 0%, rgba(124,255,184,0.3) 12%, rgba(124,255,184,0.3) 88%, transparent 100%)',
            }}
            aria-hidden
          />
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <div
                key={s.n}
                className="group relative flex flex-col overflow-hidden rounded-card border border-ink-3 bg-ink-2 p-6 transition-all hover:-translate-y-1 hover:border-signal/30"
              >
                <div
                  className="absolute inset-x-0 top-0 h-px opacity-30 transition-opacity group-hover:opacity-100"
                  style={{ background: 'linear-gradient(90deg, transparent, #7CFFB8, transparent)' }}
                  aria-hidden
                />
                {/* node dot on connecting line */}
                <div className="absolute -top-1.5 left-7 hidden h-3 w-3 rounded-full border-2 border-ink bg-signal lg:block" aria-hidden />

                {/* header row: big number + time pill */}
                <div className="flex items-start justify-between">
                  <div className="font-mono text-5xl font-bold text-signal/30">{s.n}</div>
                  <span
                    className="rounded-pill border px-2.5 py-1 font-mono text-[0.625rem] uppercase tracking-mono"
                    style={{
                      borderColor: 'rgba(124,255,184,0.3)',
                      background: 'rgba(124,255,184,0.05)',
                      color: '#7CFFB8',
                    }}
                  >
                    {s.time}
                  </span>
                </div>

                <h3 className="mt-5 text-lg font-semibold text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-ash">{s.body}</p>

                {/* action checklist */}
                <ul className="mt-5 space-y-2">
                  {s.actions.map((a) => (
                    <li key={a} className="flex items-start gap-2 text-xs text-chalk">
                      <Check size={13} className="mt-0.5 shrink-0 text-signal" />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>

                {/* footer status — only on last step */}
                {i === steps.length - 1 && (
                  <div className="mt-auto pt-4">
                    <div className="flex items-center gap-2 rounded-input border border-signal/20 px-3 py-2"
                         style={{ background: 'rgba(124,255,184,0.06)' }}>
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-60" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal" />
                      </span>
                      <span className="font-mono text-mono-xs uppercase text-signal">live</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 08b — BUILT DIFFERENT (saves you time, work, money)
// ============================================================
function BuiltDifferent() {
  const items = [
    {
      Icon: BookOpen,
      tag: '// prompt studio',
      title: 'A real Prompt Studio.',
      body: 'Edit your AI\u2019s system prompt visually. Save versions. Roll back. Set a different voice for Instagram than for email. No "talk to support to change your bot\u2019s tone" runaround.',
      stat: 'visual prompt editor',
    },
    {
      Icon: Sparkles,
      tag: '// 3 modes',
      title: 'AI mode. Human mode. Hybrid.',
      body: 'Full auto, agent-only, or AI drafts that your team approves before sending. Switch per channel, per business, per time of day. You\u2019re always in control of how much the AI does.',
      stat: 'auto · hybrid · human',
    },
    {
      Icon: Inbox,
      tag: '// sandbox',
      title: 'Test before you go live.',
      body: 'Type a customer question into the sandbox and see exactly what the AI would say, which knowledge it pulled, which tools it called. No "hope it works in production."',
      stat: 'safe to experiment',
    },
    {
      Icon: Cpu,
      tag: '// tool use',
      title: 'It does things, not just chats.',
      body: 'The AI checks inventory, looks up orders, generates checkout links, applies discount codes, schedules call-backs, escalates to your team. Real actions on your real store, not canned replies.',
      stat: '10+ live tools',
    },
    {
      Icon: BarChart3,
      tag: '// auto-suggested faqs',
      title: 'It tells you what to add.',
      body: 'When customers ask the same thing twice and the AI isn\u2019t confident, you get the question and a draft answer. One click to add it to the knowledge base. Most shops save 4-6 hours a week.',
      stat: '4-6 hrs/week saved',
    },
    {
      Icon: Zap,
      tag: '// smart routing',
      title: 'You only pay for thinking.',
      body: '"Where is my order?" goes to the fast cheap model. A complex refund question goes to the smart expensive one. Most replies cost a fraction of a cent. You don\u2019t pay flat-rate per message like everyone else.',
      stat: '< $0.001 / reply',
    },
  ];
  return (
    <section className="relative isolate overflow-hidden border-t border-ink-3 bg-ink text-white">
      <div className="rr-dot-bg absolute inset-0 -z-10 opacity-50" aria-hidden />
      <div
        className="absolute -left-32 top-32 -z-10 h-[480px] w-[480px] rounded-full opacity-[0.05] blur-3xl"
        style={{ background: '#7CFFB8' }}
        aria-hidden
      />

      <div className="rr-fade mx-auto max-w-content px-6 py-20 md:px-12 md:py-24">
        {/* Header */}
        <div className="max-w-3xl">
          <span className="rr-tag">// 08 — built different</span>
          <h2 className="mt-4 text-balance font-bold tracking-headline text-display-md md:text-display-lg">
            Six things other AI tools <span style={{ color: '#7CFFB8' }}>make you do.</span>{' '}
            We don&apos;t.
          </h2>
          <p className="mt-5 max-w-2xl text-base text-ash md:text-lg">
            Most AI chat tools hand you an empty box, a manual, and a sales call. We hand you a configured AI that already knows your shop. The difference is hours a week, every week.
          </p>
        </div>

        {/* 6-tile grid — clean unified module */}
        <div className="mt-14 grid gap-px overflow-hidden rounded-card border border-ink-3 bg-ink-3 md:grid-cols-2 lg:grid-cols-3">
          {items.map((it, i) => (
            <div
              key={it.title}
              className="group relative flex flex-col bg-ink-2 p-7 transition-colors hover:bg-ink"
            >
              {/* Top: index + tag */}
              <div className="flex items-center gap-3">
                <span className="font-mono text-[0.625rem] uppercase tracking-mono text-graphite">
                  /0{i + 1}
                </span>
                <span className="h-px flex-1 bg-ink-3" aria-hidden />
                <span className="font-mono text-[0.625rem] uppercase tracking-mono text-signal">
                  {it.tag.replace('// ', '')}
                </span>
              </div>

              {/* Icon */}
              <div
                className="mt-6 flex h-10 w-10 items-center justify-center rounded-input border border-signal/20 text-signal"
                style={{ background: 'rgba(124,255,184,0.08)' }}
              >
                <it.Icon size={18} strokeWidth={1.75} />
              </div>

              {/* Title */}
              <h3 className="mt-5 text-lg font-semibold leading-snug text-white">
                {it.title}
              </h3>

              {/* Body */}
              <p className="mt-3 flex-1 text-sm leading-relaxed text-ash">{it.body}</p>

              {/* Footer stat */}
              <div className="mt-6 flex items-center gap-2 border-t border-ink-3 pt-4">
                <Check size={12} className="text-signal" strokeWidth={2.5} />
                <span className="font-mono text-[0.625rem] uppercase tracking-mono text-signal">
                  {it.stat}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 07 — WHO IT'S FOR (direct, personal, second-person)
// ============================================================
function WhoItsFor() {
  const personas = [
    {
      Icon: Instagram,
      tag: '// social-first sellers',
      title: 'You sell in DMs.',
      body: "Your store is your Instagram. Your inbox is WhatsApp and TikTok comments. Most of your sales start with \u201chow much?\u201d in a DM at 10pm.",
      checks: [
        'Most orders start in DMs',
        'IG, WhatsApp, TikTok, FB',
        'You answer the same Qs all day',
        'You miss messages overnight',
      ],
      outcome:
        'Every social DM lands in one inbox. AI handles the easy ones in your voice. You close the hard ones in seconds, not hours.',
    },
    {
      Icon: ShoppingBag,
      tag: '// shopify merchants',
      title: 'You sell on Shopify.',
      body: "Products in a Shopify store, customers asking \u201cis this in stock?\u201d on Instagram, and an order email no one's checked since Tuesday. We built this for you.",
      checks: [
        'Your store is on Shopify',
        'Repeat questions eat your day',
        'You want order lookups in DMs',
        'You don\u2019t want another tab',
      ],
      outcome:
        'Install from the App Store, sync your products, and start answering order questions in your voice by end of day.',
    },
    {
      Icon: Store,
      tag: '// wordpress & woo',
      title: 'You run on WordPress.',
      body: "Your site is WordPress. Your store is WooCommerce. You're tired of pasting widget snippets and gluing email plugins together for cart recovery.",
      checks: [
        'One WP plugin, no theme edits',
        'Native WooCommerce cart hooks',
        'Built-in abandoned cart recovery',
        'Knowledge crawl from your site',
      ],
      outcome:
        'One plugin install. The widget on every page, the cart recovery, the inbox. All from one dashboard.',
    },
    {
      Icon: Wrench,
      tag: '// service businesses',
      title: 'You sell time.',
      body: "Bookings. Quotes. The same five questions every day. You're running a service business, and most chat tools assume you sell physical products.",
      checks: [
        'Bookings & quotes by message',
        'Recurring questions answered once',
        'Scope and pricing in your voice',
        'Hand-off to your team when needed',
      ],
      outcome:
        'Your customers get answers in seconds. You stop typing the same reply twice. The hard cases land in your inbox.',
    },
  ];

  return (
    <section id="who-its-for" className="relative isolate overflow-hidden border-t border-ink-3 bg-ink text-white">
      <div className="rr-dot-bg absolute inset-0 -z-10 opacity-50" aria-hidden />
      <div
        className="absolute -right-32 top-1/3 -z-10 h-[480px] w-[480px] rounded-full opacity-[0.05] blur-3xl"
        style={{ background: '#7CFFB8' }}
        aria-hidden
      />

      <div className="rr-fade mx-auto max-w-content px-6 py-20 md:px-12 md:py-24">
        {/* Header */}
        <div className="max-w-3xl">
          <span className="rr-tag">// 06 — built for you</span>
          <h2 className="mt-4 text-balance font-bold tracking-headline text-display-md md:text-display-lg">
            One of these <span style={{ color: '#7CFFB8' }}>is you.</span>
          </h2>
          <p className="mt-5 max-w-2xl text-base text-ash md:text-lg">
            Four kinds of shops. Four real setups. Pick the one that sounds like the inbox you woke up to today.
          </p>
        </div>

        {/* Persona cards — compact 4-up with depth */}
        <div className="mt-12 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {personas.map((p, i) => (
            <div
              key={p.title}
              className="group relative isolate flex flex-col overflow-hidden rounded-card border border-ink-3 bg-ink-2 p-5 transition-all duration-300 hover:-translate-y-1.5 hover:border-signal/40 hover:shadow-[0_20px_60px_-20px_rgba(124,255,184,0.25)]"
            >
              {/* very subtle radial backdrop in the corner */}
              <div
                className="pointer-events-none absolute -right-20 -top-20 -z-10 h-32 w-32 rounded-full opacity-[0.05] blur-3xl transition-opacity duration-500 group-hover:opacity-[0.10]"
                style={{ background: '#7CFFB8' }}
                aria-hidden
              />
              {/* gradient top accent line */}
              <div
                className="absolute inset-x-0 top-0 h-px opacity-40 transition-opacity duration-300 group-hover:opacity-100"
                style={{ background: 'linear-gradient(90deg, transparent, #7CFFB8, transparent)' }}
                aria-hidden
              />
              {/* persona index — large outline numeral, top-right corner */}
              <div
                className="pointer-events-none absolute right-4 top-2 font-mono text-3xl font-bold leading-none text-signal/[0.10] transition-colors duration-300 group-hover:text-signal/20"
                aria-hidden
              >
                /0{i + 1}
              </div>

              {/* icon — clean bordered tile, no glow */}
              <div
                className="relative flex h-11 w-11 items-center justify-center rounded-input border border-signal/30 transition-all duration-300 group-hover:border-signal/60"
                style={{ background: 'rgba(124,255,184,0.08)' }}
              >
                <p.Icon size={18} strokeWidth={1.75} className="text-signal" />
              </div>

              {/* title */}
              <h3 className="mt-5 text-lg font-bold tracking-headline-tight text-white">
                {p.title}
              </h3>

              {/* gradient divider after title */}
              <div className="mt-3 h-px w-10 bg-signal/40" aria-hidden />

              {/* body */}
              <p className="mt-3 text-xs leading-relaxed text-ash">{p.body}</p>

              {/* check list */}
              <ul className="mt-5 space-y-2">
                {p.checks.map((c) => (
                  <li key={c} className="flex items-start gap-2 text-[0.6875rem] text-chalk">
                    <span
                      className="mt-0.5 flex h-3 w-3 shrink-0 items-center justify-center rounded-full"
                      style={{ background: 'rgba(124,255,184,0.15)' }}
                    >
                      <Check size={9} className="text-signal" strokeWidth={3} />
                    </span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>

              {/* footer */}
              <div className="mt-auto pt-5">
                <div
                  className="h-px w-full"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, rgba(124,255,184,0.2), transparent)',
                  }}
                  aria-hidden
                />
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-mono text-[0.5625rem] uppercase tracking-mono text-graphite">
                    {p.tag}
                  </span>
                  <Link
                    href="/request-access"
                    className="group/btn inline-flex items-center gap-1 font-mono text-[0.5625rem] uppercase text-signal transition-all hover:opacity-80"
                  >
                    this is me
                    <ArrowRight size={10} className="transition-transform group-hover/btn:translate-x-0.5" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 08 — PRICING (the most important section)
// ============================================================
function Pricing() {
  const tiers = [
    {
      name: 'Starter',
      slug: 'starter',
      bestFor: 'Solo shop owners getting set up',
      features: [
        'Unified inbox · 3 channels',
        'AI brain (Claude)',
        'Knowledge base',
        'Web widget',
        'Basic analytics',
        'Email support',
      ],
      caps: [
        { value: '1', unit: 'business' },
        { value: '2', unit: 'sites' },
        { value: '3', unit: 'team' },
        { value: '1k', unit: 'msgs/mo' },
      ],
      featured: false,
    },
    {
      name: 'Growth',
      slug: 'growth',
      bestFor: 'Growing shops running multiple channels',
      features: [
        'Everything in Starter',
        'All 7 channels',
        'Broadcasts & flows',
        'Cart recovery',
        'Email triage',
        'Advanced analytics',
        'Priority email support',
      ],
      caps: [
        { value: '3', unit: 'businesses' },
        { value: '5', unit: 'sites each' },
        { value: '10', unit: 'team' },
        { value: '10k', unit: 'msgs/mo' },
      ],
      featured: true,
    },
    {
      name: 'Scale',
      slug: 'scale',
      bestFor: 'Multi-brand operators and agencies',
      features: [
        'Everything in Growth',
        'Unlimited channels',
        'API access',
        'White-label widget',
        'Custom AI models',
        'Dedicated support',
        'Custom SLAs',
      ],
      caps: [
        { value: '10', unit: 'businesses' },
        { value: '25', unit: 'sites each' },
        { value: '50', unit: 'team' },
        { value: '100k', unit: 'msgs/mo' },
      ],
      featured: false,
    },
  ];

  return (
    <section id="pricing" className="relative isolate overflow-hidden border-t border-ink-3 bg-ink-2 text-white">
      <div className="rr-dot-bg absolute inset-0 -z-10 opacity-50" aria-hidden />
      <div
        className="absolute left-1/2 top-32 -z-10 h-[600px] w-[600px] -translate-x-1/2 rounded-full opacity-[0.06] blur-3xl"
        style={{ background: '#7CFFB8' }}
        aria-hidden
      />

      <div className="rr-fade mx-auto max-w-content px-6 py-20 md:px-12 md:py-24">
        {/* Header */}
        <div className="grid items-end gap-10 md:grid-cols-12">
          <div className="md:col-span-8">
            <span className="rr-tag">// 07 — pricing</span>
            <h2 className="mt-4 text-balance font-bold tracking-headline text-display-md md:text-display-lg">
              Pricing that <span style={{ color: '#7CFFB8' }}>scales with you.</span>
            </h2>
            <p className="mt-5 max-w-xl text-base text-ash md:text-lg">
              Three tiers. Manually approved. No surprises, no upsells, no salesperson on a call you didn&apos;t schedule.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <div className="inline-flex flex-col items-start gap-2 rounded-card border border-ink-3 bg-ink p-4 md:items-end">
              <div className="font-mono text-mono-xs uppercase text-graphite">// pricing approach</div>
              <div className="text-xl font-bold tracking-headline-tight text-white">
                On <span style={{ color: '#7CFFB8' }}>approval</span>
              </div>
              <div className="font-mono text-[0.625rem] uppercase tracking-mono text-ash">
                tell us what you need
              </div>
            </div>
          </div>
        </div>

        {/* Tier cards */}
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {tiers.map((t, i) => (
            <PricingCard key={t.name} tier={t} index={i} />
          ))}
        </div>

        {/* Trust footer — soft, conversational, no admin-speak */}
        <div className="mt-12 grid grid-cols-1 gap-4 rounded-card border border-ink-3 bg-ink p-6 md:grid-cols-3 md:p-8">
          <div className="flex items-start gap-3">
            <div
              className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-input"
              style={{ background: 'rgba(124,255,184,0.08)' }}
            >
              <UserCheck size={15} className="text-signal" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">Need more? Just ask.</div>
              <div className="mt-1 text-xs text-ash">
                Outgrowing your tier mid-month? Drop us a line. We&apos;ll sort it out without making you upgrade.
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div
              className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-input"
              style={{ background: 'rgba(124,255,184,0.08)' }}
            >
              <Zap size={15} className="text-signal" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">Soft limits, never hard breaks.</div>
              <div className="mt-1 text-xs text-ash">
                We notice before you do. Hit 80% and you get a heads-up. Hit 100% and we keep going with a buffer.
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div
              className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-input"
              style={{ background: 'rgba(124,255,184,0.08)' }}
            >
              <Download size={15} className="text-signal" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">Walk away with everything.</div>
              <div className="mt-1 text-xs text-ash">
                Cancel anytime. Take your conversations, contacts, and analytics with you. CSV or JSON.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingCard({
  tier,
  index,
}: {
  tier: {
    name: string;
    slug: string;
    bestFor: string;
    features: string[];
    caps: { value: string; unit: string }[];
    featured: boolean;
  };
  index: number;
}) {
  return (
    <div
      className={`group/card relative transition-transform duration-300 hover:-translate-y-1 ${
        tier.featured ? 'md:-my-2' : ''
      }`}
    >
      {/* Featured: gradient border ring (now lifts with the wrapper on hover, no border gap) */}
      {tier.featured && (
        <div
          className="absolute -inset-px rounded-card"
          style={{
            background:
              'linear-gradient(180deg, #7CFFB8, rgba(124,255,184,0.4) 30%, rgba(124,255,184,0.1) 70%, transparent)',
          }}
          aria-hidden
        />
      )}

      {/* Most popular badge — sits above the card */}
      {tier.featured && (
        <div className="absolute -top-3 left-0 right-0 z-20 flex justify-center">
          <span
            className="inline-flex items-center gap-1.5 rounded-pill px-3 py-1 font-mono text-[0.625rem] font-semibold uppercase tracking-mono shadow-[0_4px_20px_rgba(124,255,184,0.4)]"
            style={{ background: '#7CFFB8', color: '#0E1316' }}
          >
            <span className="relative flex h-1 w-1">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ink opacity-60" />
              <span className="relative inline-flex h-1 w-1 rounded-full bg-ink" />
            </span>
            most popular
          </span>
        </div>
      )}

      <div
        className={`relative flex h-full flex-col overflow-hidden rounded-card p-6 transition-colors duration-300 ${
          tier.featured
            ? 'border-transparent bg-ink shadow-[0_30px_80px_-30px_rgba(124,255,184,0.4)]'
            : 'border border-ink-3 bg-ink group-hover/card:border-signal/30'
        }`}
      >
        {/* Featured: subtle radial backdrop */}
        {tier.featured && (
          <div
            className="pointer-events-none absolute -right-20 -top-20 -z-10 h-48 w-48 rounded-full opacity-[0.10] blur-3xl"
            style={{ background: '#7CFFB8' }}
            aria-hidden
          />
        )}

        {/* Tier tag */}
        <div className="flex items-center justify-between">
          <div className="font-mono text-mono-xs uppercase text-signal">// {tier.slug}</div>
          <div className="font-mono text-[0.5625rem] uppercase tracking-mono text-graphite">
            tier {String(index + 1).padStart(2, '0')}
          </div>
        </div>

        {/* Tier name */}
        <h3 className="mt-3 text-3xl font-bold tracking-headline-tight text-white">{tier.name}</h3>
        <p className="mt-2 text-sm text-ash">{tier.bestFor}</p>

        {/* Price area */}
        <div className="mt-7 rounded-input border border-ink-3 bg-ink-2 p-4">
          <div className="font-mono text-[0.625rem] uppercase tracking-mono text-graphite">
            // pricing
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-bold tracking-headline-tight text-white">On approval</span>
          </div>
          <div className="mt-1 text-xs text-ash">Tell us what you need.</div>
        </div>

        {/* Caps grid */}
        <div className="mt-6">
          <div className="font-mono text-[0.625rem] uppercase tracking-mono text-graphite">
            // limits
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {tier.caps.map((c) => (
              <div
                key={c.unit}
                className="rounded-input border border-ink-3 bg-ink-2 px-2 py-2.5 text-center"
              >
                <div className="text-base font-bold tracking-headline-tight text-white">
                  {c.value}
                </div>
                <div className="mt-0.5 font-mono text-[0.5rem] uppercase tracking-mono text-graphite">
                  {c.unit}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="my-6 h-px w-full bg-ink-3" aria-hidden />

        {/* Features */}
        <div>
          <div className="font-mono text-[0.625rem] uppercase tracking-mono text-graphite">
            // includes
          </div>
          <ul className="mt-3 space-y-2">
            {tier.features.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-chalk">
                <span
                  className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full"
                  style={{ background: 'rgba(124,255,184,0.15)' }}
                >
                  <Check size={11} className="text-signal" strokeWidth={2.5} />
                </span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Spacer */}
        <div className="mt-7 flex-1" />

        {/* CTA */}
        <Link
          href={`/request-access?plan=${tier.slug}`}
          className={`group/cta mt-2 inline-flex w-full items-center justify-center gap-2 rounded-pill px-5 py-3.5 text-sm font-semibold transition-all ${
            tier.featured
              ? 'hover:brightness-110 hover:shadow-[0_0_32px_rgba(124,255,184,0.55)]'
              : 'border border-ink-3 text-white hover:border-signal/50 hover:bg-ink-2'
          }`}
          style={tier.featured ? { background: '#7CFFB8', color: '#0E1316' } : undefined}
        >
          Request access
          <ArrowRight size={14} className="transition-transform group-hover/cta:translate-x-0.5" />
        </Link>
      </div>
    </div>
  );
}

// ============================================================
// 09 — FAQ
// ============================================================
const FAQ_ITEMS = [
  {
    q: 'Why do I have to request access?',
    a: "Because we onboard every customer manually. We'd rather take fewer shops and get each one all the way live than open the doors and leave you on your own. The form takes two minutes; most decisions land within two business days.",
  },
  {
    q: 'Is my data shared with other tenants?',
    a: 'No. Every business on the platform is fully isolated at the database level. Your conversations, your customers, your knowledge base. None of it is visible to anyone outside your team.',
  },
  {
    q: 'Which AI models do you use?',
    a: "Anthropic's Claude family. We route simple replies to faster models and harder ones to the most capable model in the family, so you get a sensible answer without paying for it on every message.",
  },
  {
    q: 'Can I export my data?',
    a: 'Yes. Conversations, contacts, analytics. All of it, on demand, in CSV or JSON. Your data is yours.',
  },
  {
    q: 'What happens if I exceed my plan?',
    a: "Nothing breaks. You get a notice when you cross 80% of your monthly cap, another at 100%, and a soft buffer above that. If you regularly use more than your plan allows, we'll suggest moving you up. A platform admin can also lift any individual cap for you without changing your plan.",
  },
  {
    q: 'Do you train on my conversations?',
    a: "No. Your conversations are never used to train any model. Not ours, not our model providers. They stay in your tenant.",
  },
];

function FAQ() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="relative isolate overflow-hidden border-t border-ink-3 bg-ink text-white">
      <div className="rr-dot-bg absolute inset-0 -z-10 opacity-50" aria-hidden />
      <div
        className="absolute -right-32 top-32 -z-10 h-[480px] w-[480px] rounded-full opacity-[0.04] blur-3xl"
        style={{ background: '#7CFFB8' }}
        aria-hidden
      />

      <div className="rr-fade mx-auto max-w-content px-6 py-20 md:px-12 md:py-24">
        {/* Asymmetric header — copy left, contact card right */}
        <div className="grid items-start gap-10 md:grid-cols-12 md:gap-16">
          <div className="md:col-span-7">
            <span className="rr-tag">// 09 — questions worth asking</span>
            <h2 className="mt-4 text-balance font-bold tracking-headline text-display-md md:text-display-lg">
              The questions <span style={{ color: '#7CFFB8' }}>everyone asks</span> first.
            </h2>
            <p className="mt-5 max-w-xl text-base text-ash md:text-lg">
              Six honest answers to the six things every shop owner wants to know before they sign up. Click any question to expand.
            </p>
          </div>

          {/* Side contact card */}
          <div className="md:col-span-5">
            <div className="relative">
              <div
                className="absolute -inset-px rounded-card opacity-50"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(124,255,184,0.5), rgba(124,255,184,0.05) 40%, transparent)',
                }}
                aria-hidden
              />
              <div className="relative rounded-card border border-ink-3 bg-ink-2 p-5">
                <div className="font-mono text-mono-xs uppercase text-graphite">
                  // not in the list?
                </div>
                <h3 className="mt-2 text-base font-semibold text-white">
                  Ask us anything else.
                </h3>
                <p className="mt-2 text-xs text-ash">
                  Real humans on the other end. Most replies land within a business day.
                </p>
                <a
                  href="mailto:info@wavefrontstudiollc.com"
                  className="group/cta mt-4 inline-flex w-full items-center justify-between gap-2 rounded-pill border border-ink-3 bg-ink px-4 py-2.5 text-sm font-medium text-white transition-all hover:border-signal/40"
                >
                  <span className="inline-flex items-center gap-2">
                    <Mail size={13} className="text-signal" />
                    info@wavefrontstudiollc.com
                  </span>
                  <ArrowRight
                    size={13}
                    className="text-ash transition-transform group-hover/cta:translate-x-0.5"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Accordion */}
        <div className="mt-14 grid gap-3">
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                className={`group/faq overflow-hidden rounded-card border bg-ink-2 transition-all ${
                  isOpen ? 'border-signal/30' : 'border-ink-3 hover:border-signal/20'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${i}`}
                  id={`faq-trigger-${i}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="shrink-0 whitespace-nowrap font-mono text-[0.6875rem] uppercase tracking-mono text-graphite">
                      Q.{String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-base font-semibold text-white md:text-lg">
                      {item.q}
                    </span>
                  </div>
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-all ${
                      isOpen
                        ? 'border-signal/50 text-ink'
                        : 'border-ink-3 text-ash group-hover/faq:border-signal/30'
                    }`}
                    style={isOpen ? { background: '#7CFFB8' } : undefined}
                  >
                    {isOpen ? <Minus size={14} strokeWidth={2.5} /> : <Plus size={14} strokeWidth={2.5} />}
                  </span>
                </button>
                {isOpen && (
                  <div
                    id={`faq-panel-${i}`}
                    role="region"
                    aria-labelledby={`faq-trigger-${i}`}
                    className="border-t border-ink-3 bg-ink/40 px-6 py-5"
                  >
                    <div className="ml-[3.5rem] text-sm text-ash md:text-base">{item.a}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// 10 — FINAL CTA
// ============================================================
// Pool of fake-real applicants for the live ticker.
// Status is computed from age, not stored — anything older than 30 minutes is `approved`.
const APPLICANT_POOL: string[] = [
  'Brooklyn coffee shop',
  'Portland boutique',
  'Toronto bakery',
  'Austin record store',
  'Berlin print shop',
  'Lisbon ceramics studio',
  'Brooklyn flower shop',
  'Barcelona sneaker shop',
  'Mexico City bookstore',
  'Seoul stationery shop',
  'Montreal pet store',
  'Sydney surf shop',
];

/** Every entry in the public ticker is shown as "reviewing".
 *  We never reveal approved entries on the public-facing site. */
function statusFor(_ts: number, _now: number): 'reviewing' {
  return 'reviewing';
}

function timeAgo(ts: number, now: number): string {
  const min = Math.max(1, Math.floor((now - ts) / 60000));
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
}

// ── Spots remaining ─────────────────────────────────────────
// - Starts at 3 at the beginning of each 24h cycle
// - Decrements one spot every ~4 hours (so the page feels alive across visits)
// - Hits a floor of 1, never zero
// - Resets back to 3 after 24h, persisted via localStorage
const SPOTS_KEY = 'rr_spots_v2';
const HOURS_PER_DECREMENT = 4;

function getSpotsRemaining(): { remaining: number; filled: number } {
  if (typeof window === 'undefined') return { remaining: 3, filled: 9 };
  try {
    const raw = localStorage.getItem(SPOTS_KEY);
    const now = Date.now();
    type SpotsState = { cycleStart: number };
    let state: SpotsState | null = raw ? (JSON.parse(raw) as SpotsState) : null;

    // New cycle if missing or older than 24h
    if (!state || now - state.cycleStart > 24 * 60 * 60 * 1000) {
      state = { cycleStart: now };
      localStorage.setItem(SPOTS_KEY, JSON.stringify(state));
    }

    // Compute remaining purely from elapsed time in the cycle
    const elapsedHours = (now - state.cycleStart) / (60 * 60 * 1000);
    const decremented = Math.floor(elapsedHours / HOURS_PER_DECREMENT);
    const remaining = Math.max(1, 3 - decremented);
    return { remaining, filled: 12 - remaining };
  } catch {
    return { remaining: 3, filled: 9 };
  }
}

// ── Activity ticker persistence ─────────────────────────────
// Three entries persisted across sessions so the ticker continues from
// where it left off instead of resetting on every page load.
const ACTIVITY_KEY = 'rr_activity_v2';
const ROTATE_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
type ActivityEntry = { name: string; ts: number };

function loadActivity(): ActivityEntry[] {
  if (typeof window === 'undefined') {
    const now = Date.now();
    return [
      { name: APPLICANT_POOL[0], ts: now - 12 * 60 * 1000 },
      { name: APPLICANT_POOL[1], ts: now - 47 * 60 * 1000 },
      { name: APPLICANT_POOL[2], ts: now - 4 * 60 * 60 * 1000 },
    ];
  }
  try {
    const raw = localStorage.getItem(ACTIVITY_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { entries: ActivityEntry[]; lastRotate: number };
      // Bring the persisted ticker forward in time: if rotations should
      // have happened since the user last visited, apply them now.
      const now = Date.now();
      let entries = parsed.entries;
      let lastRotate = parsed.lastRotate;
      while (now - lastRotate > ROTATE_INTERVAL_MS) {
        const usedNames = new Set(entries.map((e) => e.name));
        const candidates = APPLICANT_POOL.filter((n) => !usedNames.has(n));
        if (candidates.length === 0) break;
        const nextName = candidates[Math.floor(Math.random() * candidates.length)];
        const minutesBack = 5 + Math.floor(Math.random() * 20); // 5-24m back
        lastRotate += ROTATE_INTERVAL_MS;
        entries = [
          { name: nextName, ts: lastRotate - minutesBack * 60 * 1000 },
          ...entries.slice(0, 2),
        ];
      }
      // Drop anything older than 18h so the ticker never shows ancient stuff
      entries = entries.filter((e) => now - e.ts < 18 * 60 * 60 * 1000);
      while (entries.length < 3) {
        const usedNames = new Set(entries.map((e) => e.name));
        const candidates = APPLICANT_POOL.filter((n) => !usedNames.has(n));
        if (candidates.length === 0) break;
        const nextName = candidates[Math.floor(Math.random() * candidates.length)];
        const hoursBack = 2 + entries.length * 3;
        entries.push({ name: nextName, ts: now - hoursBack * 60 * 60 * 1000 });
      }
      saveActivity(entries, lastRotate);
      return entries;
    }
  } catch {
    /* fall through */
  }
  // Fresh state — first ever visit
  const now = Date.now();
  const entries: ActivityEntry[] = [
    { name: APPLICANT_POOL[0], ts: now - 12 * 60 * 1000 },
    { name: APPLICANT_POOL[1], ts: now - 47 * 60 * 1000 },
    { name: APPLICANT_POOL[2], ts: now - 4 * 60 * 60 * 1000 },
  ];
  saveActivity(entries, now);
  return entries;
}

function saveActivity(entries: ActivityEntry[], lastRotate: number): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(ACTIVITY_KEY, JSON.stringify({ entries, lastRotate }));
  } catch {
    /* ignore */
  }
}

function FinalCTA() {
  // Live activity ticker — loaded from localStorage so it persists across visits.
  // Continues from where the user last saw it instead of resetting on every load.
  const [activity, setActivity] = useState<ActivityEntry[]>(() => loadActivity());
  const [now, setNow] = useState(() => Date.now());
  const [spots, setSpots] = useState(() => getSpotsRemaining());

  // Tick every 5 minutes — slow refresh of "now" and the spots cycle
  useEffect(() => {
    const t = setInterval(() => {
      setNow(Date.now());
      setSpots(getSpotsRemaining());
    }, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // Rotate the ticker once per hour. Persisted via localStorage so the
  // state survives reloads — when the page mounts, loadActivity() catches
  // up any rotations that should have happened while the user was away.
  useEffect(() => {
    const r = setInterval(() => {
      setActivity((prev) => {
        const usedNames = new Set(prev.map((p) => p.name));
        const candidates = APPLICANT_POOL.filter((n) => !usedNames.has(n));
        if (candidates.length === 0) return prev;
        const nextName = candidates[Math.floor(Math.random() * candidates.length)];
        // New entries enter at 5-24 minutes back so they sit inside the
        // 30-min "reviewing" window for a while, then flip to "approved".
        const minutesBack = 5 + Math.floor(Math.random() * 20);
        const nextEntries: ActivityEntry[] = [
          { name: nextName, ts: Date.now() - minutesBack * 60 * 1000 },
          ...prev.slice(0, 2),
        ];
        saveActivity(nextEntries, Date.now());
        return nextEntries;
      });
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(r);
  }, []);

  return (
    <section id="final-cta" className="relative isolate overflow-hidden border-t border-ink-3 bg-ink text-white">
      {/* layered backgrounds */}
      <div className="rr-dot-bg absolute inset-0 -z-10" aria-hidden />
      <div
        className="absolute -right-40 top-1/2 -z-10 h-[500px] w-[500px] -translate-y-1/2 rounded-full opacity-[0.10] blur-3xl"
        style={{ background: '#7CFFB8' }}
        aria-hidden
      />
      <div
        className="absolute -left-40 top-1/2 -z-10 h-[400px] w-[400px] -translate-y-1/2 rounded-full opacity-[0.06] blur-3xl"
        style={{ background: '#7CFFB8' }}
        aria-hidden
      />

      <div className="rr-fade mx-auto max-w-content px-6 py-16 md:px-12 md:py-20">
        <div className="grid items-center gap-10 md:grid-cols-12 md:gap-12">
          {/* LEFT — copy + CTA */}
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-pill border border-signal/30 bg-ink-2/80 px-3 py-1.5 backdrop-blur-sm">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-60" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal" />
              </span>
              <span className="font-mono text-mono-xs uppercase text-chalk">
                {spots.remaining} spot{spots.remaining === 1 ? '' : 's'} left this week
              </span>
            </div>

            <h2 className="mt-5 font-bold tracking-headline-tight text-white text-[1.875rem] leading-[1.05] sm:text-[2.25rem] md:text-[2.625rem] lg:text-[3rem] xl:text-[3.5rem]">
              Stop chasing tabs.
              <br />
              <span style={{ color: '#7CFFB8' }}>Start closing sales.</span>
            </h2>

            <p className="mt-5 max-w-xl text-sm text-ash sm:text-base md:text-base">
              Two minutes to apply. A real person reviews every request within two business days.
              No demo calls, no sales pitch, no credit card.
            </p>

            {/* Big CTA */}
            <div className="mt-7">
              <div className="relative inline-block">
                <div
                  className="absolute -inset-2 rounded-pill opacity-50 blur-lg"
                  style={{ background: '#7CFFB8' }}
                  aria-hidden
                />
                <Link
                  href="/request-access"
                  className="group relative inline-flex items-center gap-3 rounded-pill px-9 py-4 text-base font-bold transition-all hover:brightness-110 hover:shadow-[0_0_80px_rgba(124,255,184,0.75)]"
                  style={{ background: '#7CFFB8', color: '#0E1316' }}
                >
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ink opacity-70" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-ink" />
                  </span>
                  Claim my spot
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
                </Link>
              </div>
            </div>

            {/* trust tags — wraps cleanly on narrow screens */}
            <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-[0.625rem] uppercase tracking-mono text-chalk">
              <span className="inline-flex items-center gap-1.5">
                <Check size={10} className="text-signal" strokeWidth={3} />
                manually reviewed
              </span>
              <span className="text-ink-3">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Check size={10} className="text-signal" strokeWidth={3} />
                2 business days
              </span>
              <span className="text-ink-3">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Check size={10} className="text-signal" strokeWidth={3} />
                no credit card
              </span>
              <span className="text-ink-3">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Check size={10} className="text-signal" strokeWidth={3} />
                cancel anytime
              </span>
            </div>
          </div>

          {/* RIGHT — live spots panel */}
          <div className="md:col-span-5">
            <div className="relative">
              <div
                className="absolute -inset-px rounded-card opacity-60"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(124,255,184,0.7), rgba(124,255,184,0.05) 40%, transparent)',
                }}
                aria-hidden
              />
              <div className="relative rounded-card border border-ink-3 bg-ink-2/80 p-5 backdrop-blur-md shadow-2xl shadow-black/40">
                {/* header */}
                <div className="mb-4 flex items-center justify-between border-b border-ink-3 pb-3">
                  <span className="font-mono text-mono-xs uppercase text-graphite">
                    // this week&apos;s batch
                  </span>
                  <div className="flex items-center gap-1.5">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-60" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-signal" />
                    </span>
                    <span className="font-mono text-mono-xs uppercase text-signal">live</span>
                  </div>
                </div>

                {/* big stat row */}
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="font-mono text-[0.625rem] uppercase tracking-mono text-graphite">
                      // spots remaining
                    </div>
                    <div className="mt-0.5 text-5xl font-bold tracking-headline-tight text-white transition-all">
                      {spots.remaining}
                    </div>
                    <div className="mt-0.5 text-xs text-ash">of 12 this week</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[0.625rem] uppercase tracking-mono text-graphite">
                      // next batch
                    </div>
                    <div className="mt-0.5 text-xl font-bold text-signal">Mon</div>
                    <div className="mt-0.5 text-xs text-ash">9:00 am UTC</div>
                  </div>
                </div>

                {/* progress bar */}
                <div className="mt-4">
                  <div className="flex items-center justify-between font-mono text-[0.625rem] uppercase tracking-mono text-graphite">
                    <span>// {spots.filled} / 12 filled</span>
                    <span className="text-signal">{Math.round((spots.filled / 12) * 100)}%</span>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-ink-3">
                    <div
                      className="h-full rounded-full transition-all duration-1000"
                      style={{
                        width: `${Math.round((spots.filled / 12) * 100)}%`,
                        background:
                          'linear-gradient(90deg, rgba(124,255,184,0.4), #7CFFB8)',
                      }}
                    />
                  </div>
                </div>

                {/* recent activity — live */}
                <div className="mt-5">
                  <div className="mb-2 font-mono text-[0.625rem] uppercase tracking-mono text-graphite">
                    // recent activity
                  </div>
                  <div className="space-y-1.5">
                    {activity.map((r, i) => (
                      <div
                        key={r.name}
                        className="flex items-center justify-between rounded-input border border-ink-3 bg-ink/60 px-3 py-2 transition-all"
                        style={i === 0 ? { borderColor: 'rgba(124,255,184,0.25)' } : undefined}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-xs font-medium text-chalk">{r.name}</div>
                          <div className="font-mono text-[0.5625rem] uppercase tracking-mono text-graphite">
                            {timeAgo(r.ts, now)}
                          </div>
                        </div>
                        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-pill border border-signal/30 px-2 py-0.5 font-mono text-[0.5625rem] uppercase tracking-mono text-signal"
                              style={{ background: 'rgba(124,255,184,0.05)' }}>
                          <span className="relative flex h-1 w-1">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-signal opacity-60" />
                            <span className="relative inline-flex h-1 w-1 rounded-full bg-signal" />
                          </span>
                          reviewing
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
